/**
 * UpgradePrompt — plan-upgrade call to action shown in place of a plan-gated feature.
 * @layer widget
 * @package @plerity/dashboard-shared
 *
 * Replaces the byte-identical email + sms `src/components/UpgradePrompt.tsx` copies
 * (cross-app duplication ratchet, 2026-10-23). The feature catalogue stays app-owned:
 * each app passes its own `FEATURE_MATRIX` via `features`, so the shared widget never
 * imports app code. Copy, gating text and styling now live in one place.
 *
 *   <UpgradePrompt featureId="automations" features={FEATURE_MATRIX} />
 *
 * Lookup contract (unchanged from the app copies):
 *   - a known `featureId` renders that entry's `label` + `description`
 *   - an unknown id renders the id itself + a generic "requires a plan upgrade" line
 */

/** Minimal shape of an app's feature-matrix entry. Extra fields are ignored. */
export interface UpgradePromptFeature {
  id: string;
  label: string;
  description?: string;
}

export interface UpgradePromptProps {
  /** Feature id to describe. Looked up in `features`. */
  featureId: string;
  /** The app's feature catalogue (e.g. `FEATURE_MATRIX` from `src/lib/features.ts`). */
  features?: ReadonlyArray<UpgradePromptFeature>;
  /** Plan named in the "Available on the … plan" line. Default `Professional`. */
  planName?: string;
  /** Where the Upgrade Plan button goes. Default: the Hub billing page. */
  upgradeUrl?: string;
  /** Rendered as `data-help-context` so the help system can anchor to the prompt. */
  helpContext?: string;
  className?: string;
}

/** Hub billing page — the single place a plan upgrade happens. */
export const HUB_BILLING_URL = "https://app.animasmarketing.com/billing"; // @allow-hardcoded

const GENERIC_DESCRIPTION = "This feature requires a plan upgrade.";

export function UpgradePrompt({
  featureId,
  features = [],
  planName = "Professional",
  upgradeUrl = HUB_BILLING_URL,
  helpContext,
  className = "",
}: UpgradePromptProps) {
  const gate = features.find((feature) => feature.id === featureId);
  const featureLabel = gate?.label ?? featureId;
  const featureDescription = gate?.description ?? GENERIC_DESCRIPTION;

  return (
    <div
      data-help-context={helpContext}
      className={`mx-auto max-w-lg overflow-hidden rounded-lg border border-light bg-surface shadow-sm ${className}`}
    >
      {/* Brand accent stripe */}
      <div className="h-1 w-full bg-primary-600 dark:bg-primary-500" />
      <div className="p-8 text-center">
        <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-900/30">
          <svg
            className="h-7 w-7 text-primary-600 dark:text-primary-400"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
            />
          </svg>
        </div>

        <h2 className="mb-2 text-xl font-semibold text-fg">{featureLabel}</h2>

        <p className="mb-5 text-gray-600 dark:text-gray-400">{featureDescription}</p>

        <p className="mb-6 text-sm font-medium text-primary-600 dark:text-primary-400">
          Available on the <strong>{planName}</strong> plan
        </p>

        <a
          href={upgradeUrl}
          className="inline-flex items-center rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:bg-primary-500 dark:hover:bg-primary-600"
        >
          Upgrade Plan
          <svg
            className="ml-2 h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
          </svg>
        </a>
      </div>
    </div>
  );
}
